import { Container, Graphics } from "pixi.js";
import { COLORS } from "@golazo/shared";
import { JUICE } from "./config.js";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: number;
  gravity: number;
}

export interface Particles {
  container: Container;
  burst(x: number, y: number, count: number, options?: BurstOptions): void;
  confetti(x: number, y: number, teamColor: number): void;
  /** Anillo expansivo suave (pases, posesion). */
  ring(x: number, y: number, color: number, maxRadius?: number): void;
  update(dt: number): void;
}

interface BurstOptions {
  speed?: number;
  life?: number;
  size?: number;
  color?: number;
  spread?: number;
  gravity?: number;
}

interface RingParticle {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  maxRadius: number;
  color: number;
}

export function createParticles(): Particles {
  const container = new Container();
  container.label = "particles";

  const gfx = new Graphics();
  container.addChild(gfx);

  const pool: Particle[] = [];
  const rings: RingParticle[] = [];
  let write = 0;

  const spawn = (p: Omit<Particle, "life" | "maxLife"> & { life: number }): void => {
    if (write >= JUICE.PARTICLES.MAX) write = 0;
    const slot = pool[write];
    if (slot) {
      Object.assign(slot, p, { maxLife: p.life });
    } else {
      pool[write] = { ...p, maxLife: p.life };
    }
    write += 1;
  };

  return {
    container,

    burst(x: number, y: number, count: number, options: BurstOptions = {}): void {
      const speed = options.speed ?? 420;
      const life = options.life ?? JUICE.PARTICLES.KICK_LIFE;
      const size = options.size ?? 5;
      const color = options.color ?? COLORS.BALL;
      const spread = options.spread ?? Math.PI * 2;
      const gravity = options.gravity ?? 0;

      for (let i = 0; i < count; i++) {
        const angle = Math.random() * spread - spread / 2;
        const mag = speed * (0.35 + Math.random() * 0.65);
        spawn({
          x,
          y,
          vx: Math.cos(angle) * mag,
          vy: Math.sin(angle) * mag,
          life,
          size: size * (0.6 + Math.random() * 0.8),
          color,
          gravity,
        });
      }
    },

    confetti(x: number, y: number, teamColor: number): void {
      const colors = [teamColor, COLORS.HUD_TEXT, 0xffd76e, 0xffffff];
      for (let i = 0; i < 90; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
        const mag = 260 + Math.random() * 520;
        spawn({
          x: x + (Math.random() - 0.5) * 80,
          y: y + (Math.random() - 0.5) * 40,
          vx: Math.cos(angle) * mag,
          vy: Math.sin(angle) * mag,
          life: JUICE.PARTICLES.GOAL_LIFE * (0.6 + Math.random() * 0.5),
          size: 4 + Math.random() * 7,
          color: colors[i % colors.length]!,
          gravity: 620,
        });
      }
    },

    ring(x: number, y: number, color: number, maxRadius = 28): void {
      rings.push({ x, y, life: 0.28, maxLife: 0.28, maxRadius, color });
    },

    update(dt: number): void {
      gfx.clear();
      let alive = 0;

      for (let i = rings.length - 1; i >= 0; i--) {
        const ring = rings[i]!;
        ring.life -= dt;
        if (ring.life <= 0) {
          rings.splice(i, 1);
          continue;
        }
        const t = 1 - ring.life / ring.maxLife;
        const radius = ring.maxRadius * t;
        const alpha = (1 - t) * 0.45;
        gfx.circle(ring.x, ring.y, radius).stroke({ width: 2, color: ring.color, alpha });
        alive += 1;
      }

      for (const p of pool) {
        if (!p || p.life <= 0) continue;

        p.life -= dt;
        if (p.life <= 0) continue;

        p.vy += p.gravity * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= Math.exp(-4 * dt);

        const t = p.life / p.maxLife;
        const alpha = t * t;
        gfx.circle(p.x, p.y, p.size * (0.4 + 0.6 * t)).fill({ color: p.color, alpha });
        alive += 1;
      }

      container.visible = alive > 0;
    },
  };
}
